import { memo, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { NewRedirectIcon } from 'components/customicons/holdings';
import Render from 'components/render';
import StripPortal, { STRIPS_ORDER } from 'components/StripPortal';
import {
  TWENTY_FOUR_HOURS,
  FIFTEEN_MINUTES,
  ONE_HOUR,
  UNDER_MAINTENANCE_URL,
} from 'constants/constants';
import { TIME_FORMATS } from 'constants/timeformats';
import {
  dateFormat,
  getDateTime,
  getDiff,
  getSeconds,
  startOf,
  timeNow,
  toLocal,
  toUTC,
} from 'helpers/day';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { activeTabSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import { SmallRectangle, CloseIcon } from 'styles/assets/icons';

import { MaintenanceStripProps } from '../DesktopView';
import styles from '../maintenanceBanner.module.scss';
import useMaintenance from '../useMaintenance';

const MobileView = (props: MaintenanceStripProps) => {
  const { t } = useTranslation(['common']);

  const drawerTab = useAppSelector(activeTabSelector);

  const shouldDisplayInMobileTab = [
    APP_DRAWER_TAB_ID_MAPPING.HOME,
    APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
    APP_DRAWER_TAB_ID_MAPPING.TRADE,
    APP_DRAWER_TAB_ID_MAPPING.MARKETS,
  ].includes(drawerTab);

  const isStickyTab = [
    APP_DRAWER_TAB_ID_MAPPING.TRADE,
    APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
  ].includes(drawerTab);

  const {
    isLoggedIn,
    showBanner,
    setShowBanner,
    maintenanceStartTime,
    maintenanceFinishTime,
    isDatesEqual,
    maintenanceDuration,
    timeLeftToStartMaintenance,
    isVisibleToUsers,
    countdownRef,
    maintenance_start_time: startTime,
    maintenanceFinishTimeInMilliseconds,
    maintenance_announcement_time: announcementTime,
    countdownTimer,
  } = useMaintenance();

  const { isTabInactive } = useAppSelector(state => state.other);
  const [isClosable, setClosable] = useState(true);

  const [bannerClosed, setBannerClosed] = useState(
    // eslint-disable-next-line no-unneeded-ternary
    localStorage.getItem('maintenanceStripClosingTime') ? true : false
  );

  useEffect(() => {
    if (localStorage.getItem('maintenanceStripClosingTime') && announcementTime) {
      const closingTime =
        localStorage.getItem('maintenanceStripClosingTime') &&
        JSON.parse(localStorage.getItem('maintenanceStripClosingTime'));

      if (Number(closingTime) * 1000 < Number(announcementTime)) {
        localStorage.removeItem('maintenanceStripClosingTime');
        setBannerClosed(false);
      }
    }
  }, [announcementTime]);

  useEffect(() => {
    setBannerVisibility();
  }, [startTime, drawerTab, maintenanceDuration]);

  useEffect(() => {
    showCrossButton();
  }, [startTime, drawerTab, countdownTimer]);

  useEffect(() => {
    const maintenanceStripClosingTime =
      localStorage.getItem('maintenanceStripClosingTime') &&
      JSON.parse(localStorage.getItem('maintenanceStripClosingTime'));

    const currentTime = timeNow();
    const timeDiff = getDiff(
      currentTime,
      getDateTime(maintenanceStripClosingTime),
      'seconds'
    );

    if (timeDiff >= 2 * ONE_HOUR) {
      setBannerClosed(false);
      localStorage.removeItem('maintenanceStripClosingTime');
    }
  }, [isTabInactive, drawerTab, startTime]);

  const setBannerVisibility = () => {
    setShowBanner(false);
    if (maintenanceDuration <= ONE_HOUR) {
      if (
        drawerTab === 'markets' &&
        timeLeftToStartMaintenance > 0 &&
        timeLeftToStartMaintenance <= TWENTY_FOUR_HOURS
      ) {
        setShowBanner(true);
      } else if (
        drawerTab !== 'markets' &&
        timeLeftToStartMaintenance > 0 &&
        timeLeftToStartMaintenance <= 12 * ONE_HOUR
      ) {
        setShowBanner(true);
      }
    } else if (maintenanceDuration <= 4 * ONE_HOUR) {
      if (
        drawerTab === 'markets' &&
        timeLeftToStartMaintenance > 0 &&
        timeLeftToStartMaintenance <= 2 * TWENTY_FOUR_HOURS
      ) {
        setShowBanner(true);
      } else if (
        drawerTab !== 'markets' &&
        timeLeftToStartMaintenance > 0 &&
        timeLeftToStartMaintenance <= TWENTY_FOUR_HOURS
      ) {
        setShowBanner(true);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (
        drawerTab === 'markets' &&
        timeLeftToStartMaintenance > 0 &&
        timeLeftToStartMaintenance <= 3 * TWENTY_FOUR_HOURS
      ) {
        setShowBanner(true);
      } else if (
        drawerTab !== 'markets' &&
        timeLeftToStartMaintenance > 0 &&
        timeLeftToStartMaintenance <= 2 * TWENTY_FOUR_HOURS
      ) {
        setShowBanner(true);
      }
    }
  };

  const storeMaintenanceStripCloseTime = () => {
    const currentTime = new Date().getTime();
    localStorage.setItem('maintenanceStripClosingTime', currentTime.toString());
  };
  const onCloseIconClick = () => {
    setBannerClosed(true);
    storeMaintenanceStripCloseTime();
  };

  const showCrossButton = () => {
    if (maintenanceDuration <= ONE_HOUR) {
      if (
        drawerTab === 'markets' &&
        countdownRef.current >= 0 &&
        countdownRef.current <= FIFTEEN_MINUTES
      ) {
        setClosable(false);
      } else {
        setClosable(true);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (
        drawerTab === 'markets' &&
        countdownRef.current > 0 &&
        countdownRef.current <= 2 * FIFTEEN_MINUTES
      ) {
        setClosable(false);
      } else {
        setClosable(true);
      }
    }
  };

  const show =
    showBanner &&
    shouldDisplayInMobileTab &&
    (isLoggedIn || isVisibleToUsers) &&
    !bannerClosed;

  return (
    (<StripPortal isSticky={isStickyTab}>
      <Render when={show}>
        <div
          className={`${styles.maintenanceWrapper} ${
            props.isTopBanner ? styles.topBanner : ''
          } ${props.className || ''}
          `}
          style={{
            order: STRIPS_ORDER.MAINTENANCE_STRIP,
          }}
        >
          <div className={styles.desc}>
            <div className={styles.title}>
              <div className={styles.icon}>
                <SmallRectangle className="info-icon" />
              </div>
              <div className={styles.heading}>
                {t('common:maintenance_notif_banner_new')}
                <span className={`${styles.redirectLink}`}>
                  <a
                    data-testid="under-maintenance-url"
                    href={`${UNDER_MAINTENANCE_URL}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <NewRedirectIcon />
                  </a>
                </span>
              </div>
            </div>

            <div className={styles.text}>
              {countdownRef.current >= 0 &&
              countdownRef.current <= 2 * FIFTEEN_MINUTES ? (
                <div className={styles.timer} data-palette="MobileView">
                  {t('common:startsIn')}
                  {dateFormat(
                    getSeconds(startOf(timeNow(), 'day'), countdownRef.current),
                    TIME_FORMATS.mmss
                  )}
                </div>
              ) : (
                <div className={styles.timer} data-palette="MobileView">
                  {maintenanceStartTime}
                  {` `}
                  {!isDatesEqual ? t('common:to_small') : '-'}{' '}
                  {isDatesEqual
                    ? dateFormat(
                        toLocal(toUTC(getDateTime(maintenanceFinishTimeInMilliseconds))),
                        TIME_FORMATS.LT
                      )
                    : maintenanceFinishTime}{' '}
                  ({new Intl.DateTimeFormat().resolvedOptions().timeZone})
                </div>
              )}
            </div>
          </div>
          {isClosable && (
            <div
              aria-hidden="true"
              data-testid="close-icon"
              className={styles.close}
              onClick={onCloseIconClick}
              data-palette="MobileView">
              <CloseIcon />
            </div>
          )}
        </div>
      </Render>
    </StripPortal>)
  );
};

export default memo(MobileView);
